import { isEqual } from 'lodash-es';
import { getCascadeOptionsNew } from '@intelligence/components/ExportTable/components/CustomForm';
import { BusinessType } from '@intelligence/components/ExportTable/types';
import { IChildProp } from '@intelligence/components/Form/type';
import downloadConfig from '@intelligence/_config/components/downloads/index';

/**
 * 针对配置文件中specialSelected的特殊字段
 * @param type
 * @param params
 * @returns
 */
export const getSelectedFields = (type: any, params: any = {}) => {
  switch (type) {
    case 'pc-store-rank-more':
      return {
        app_ids: params?.select_ids?.map((item: any) => item[params?.selectedIdType]) || [],
      };
    case 'streaming-games-top':
    case 'streaming-anchors-more':
      return {
        select_ids: params?.select_ids,
      };
    default:
      return {};
  }
};

// marketProfile-stats的tableParams传递下来的download_metrics默认值处理
export const getDownloadMetricsByType = (dataType: string) => {
  switch (dataType) {
    case 'operation':
      return ['operation'];
    case 'game':
      return ['games'];
    case 'genre':
      return ['main_genres', 'sub_genres'];
    default:
      return ['companies'];
  }
};

// 转换函数-sourceMetrics
export const transformData = (data: any) => {
  try {
    const newData = JSON.parse(JSON.stringify(data));
    const elementsToMove = ['steamfollowers', 'steamreviews'];

    elementsToMove.forEach((element) => {
      if (Array.isArray(newData.steamcharts)) {
        const index = newData.steamcharts.indexOf(element);
        if (index !== -1) {
          // 从steamcharts移除
          newData.steamcharts.splice(index, 1);
          // 检查newData.vginsights是否为数组，不是则初始化为数组
          newData.vginsights = Array.isArray(newData.vginsights) ? newData.vginsights : [];
          // 添加到vginsights
          newData.vginsights.push(element);
        }
      }
    });

    return newData;
  } catch (err) {
    console.error('An error occurred during data transformation:', err);
    return data; // if an error occurred return the original data
  }
};

/**
 * 通过 Metrics 选项框顺序给 download_metrics 排序
 * @param data 下载表单对象
 * @param formList 表单展示项，从中筛选出 metrics options
 */
export const sortDownloadMetricsByOptionsOrder = (
  data: any,
  formList: { field: string; options: [] }[],
) => {
  const metricsOptions = formList.find((item) => item.field === 'download_metrics')?.options;

  if (!metricsOptions?.length) return;

  // 获取选项顺序 map
  const metricsOptionsOrderMap = metricsOptions?.reduce(
    (prev: Record<string, number>, curr: { value: string }, index: number) => {
      prev[curr.value] = index;
      return prev;
    },
    {},
  );

  // metrics 指标排序
  data.download_metrics.sort(
    (a: string, b: string) => metricsOptionsOrderMap[a] - metricsOptionsOrderMap[b],
  );
};

/**
 * 合并其他数据源的device的options集合、GSD数据源的device的options集合
 * @param deviceSources 其他数据源的集合
 * @param gsdDeviceOptions GSD数据源
 * @returns
 */
export const composeDeviceOptionsAndGsdDeviceOptions = <T extends Record<string, any>>(
  deviceSources?: T[],
  gsdDeviceSource?: T,
): T[] => {
  // 数据异常，返回空数组
  if (!Array.isArray(deviceSources) && !gsdDeviceSource) return [];
  // GSD数据源的device的options集合异常，直接返回其他数据源的device的options集合
  if (!gsdDeviceSource) return deviceSources as T[];
  // 其他数据源的device的options集合异常，直接返回GSD数据源的device的options集合
  if (!Array.isArray(deviceSources)) return [gsdDeviceSource];
  // 其他数据源的device的options集合、GSD数据源的device的options集合 都是正常数据，进行组合，GSD数据源放在Mscience数据源的前面
  const mscienceDeviceSourceIdx = deviceSources.findIndex((item) => item?.value === 'mscience');
  if (mscienceDeviceSourceIdx === -1) return [...deviceSources, gsdDeviceSource];
  return [
    ...deviceSources.slice(0, mscienceDeviceSourceIdx),
    gsdDeviceSource,
    ...deviceSources.slice(mscienceDeviceSourceIdx),
  ];
};

/**
 * 更新表单控件的options的disabled状态、defaultValue（仅更新级联指标筛选器表单控件）
 * @param controls 表单控件集合
 * @param payload.pageId 页面Id
 * @param payload.tableParams 页面传入的榜单入参
 * @returns 表单控件集合（更新表单控件的options的disabled状态、defaultValue）（仅更新级联指标筛选器表单控件）
 */
export const updateCascaderMetricOptionsDisabledStateAndDefaultValue = (
  controls?: any[],
  payload?: {
    pageId?: string;
    tableParams?: Record<string, any>;
  },
): any[] => {
  if (!Array.isArray(controls) || !controls.length) return [];
  return controls.map((control) => {
    // 非级联指标筛选器控件 不做更新
    // console.log('control?.cascadeOptions', control?.cascadeOptions);
    if (
      control?.type === 'sourceMetricSelectorDetail' ||
      control?.type === 'cascadeMetricSelector'
    ) {
      const cascadeOptions = getCascadeOptionsNew(control?.cascadeOptions, {
        ifUseMetricsMap: control?.ifUseMetricsMap || false,
        pageId: payload?.pageId,
        tableParams: payload?.tableParams,
      });
      let defaultValue: [string, string][] = control?.defaultValue;
      // console.log('cascadeOptionscascadeOptions', cascadeOptions);
      // defaultValue不是配置的全选时（即配置为[['All', 'All']]），需要处理value的有效性
      if (!isEqual([['All', 'All']], defaultValue)) {
        // 处理defaultValue，在原有defaultValue配置的基础上仅保留能匹配上选项option的 并且 匹配上的option非禁用的
        defaultValue = control?.defaultValue?.filter((dataSourceMetricValue: [string, string]) => {
          const [dataSource, metric] = dataSourceMetricValue || [];
          const cascadeOption = cascadeOptions?.find(
            (cascadeOption) => cascadeOption?.value === dataSource,
          );
          // 当前value的数据源没匹配到cascadeOption 或者 当前value的数据源匹配的cascadeOption是禁用的，过滤掉当前value
          if (!cascadeOption || cascadeOption?.disabled) return false;
          const subCascadeOption = cascadeOption.children?.find(
            (subCascadeOption: any) => subCascadeOption?.value === metric,
          );
          return subCascadeOption && !subCascadeOption?.disabled;
        });
      }
      // console.log('defaultValuedefaultValue', defaultValue);
      return {
        ...control,
        cascadeOptions: cascadeOptions || [],
        defaultValue: defaultValue || [],
      };
    }
    return { ...control };
  });
};

/**
 * 把下载表单对象中的指定的value为boolean类型的字段 转换为 1、0
 * @param data 下载表单对象
 * @param fields 需要把boolean转换为1（true）、0（false）的字段列表列表
 */
export const convertBoolean2Num = (data: any, fields: string[]) => {
  if (Object.prototype.toString.call(data) !== '[object Object]') return;
  if (!Array.isArray(fields)) return;

  for (const field of fields) {
    if (!Reflect.has(data, field)) continue;
    const fieldValue = Reflect.get(data, field);
    if (typeof fieldValue !== 'boolean') continue;
    Reflect.set(data, field, fieldValue ? 1 : 0);
  }
};

// 获取下载配置
export const getDownloadSet = (businessType: BusinessType) => {
  // TODO：有点浪费性能
  const DOWNLOAD_SET_MAP: Record<BusinessType, IChildProp[]> = {
    'mobile-stats-retentionmonthly': downloadConfig.mobileDetailStatsAndCountries,
    'mobile-stats-retentionlifetime': downloadConfig.mobileDetailStatsAndCountries,
    'mobile-stats-countries-rpd': downloadConfig.mobileDetailStatsAndCountries,
    'mobile-stats-countries': downloadConfig.mobileDetailStatsAndCountries,
    'mobile-details-store-rank-trend': downloadConfig.mobileDetailStoreRank,
    'pc-stats-countries-pcu': downloadConfig.PCDetailStatsAndCountries,
    'pc-stats-countries': downloadConfig.PCDetailStatsAndCountries,
    'pc-details-store-rank': downloadConfig.PCDetailStoreRank,
    'console-MetricsTable': downloadConfig.consoleMetricsTable,
    'console-DigitalRevenue': downloadConfig.consoleDigitalRevenue,
    'console-PhysicalRevenue': downloadConfig.consoleUniversal,
    'topcharts-console-japan-sales': downloadConfig.consoleUniversal,
    'console-MAU': downloadConfig.consoleUniversal,
    'console-LifeTimePlayers': downloadConfig.consoleOthers,
    'console-newRelease': downloadConfig.consoleOthers,
    'console-upcoming': downloadConfig.consoleOthers,
    'console-stats-countries': downloadConfig.consoleDetailStatsAndCountries,
    'console-stats-countries-japan-sales': downloadConfig.consoleDetailJapanSales,
    'console-details-store-rank': downloadConfig.consoleDetailStoreRank,
    'combine-PCConsoleMetricsTable': downloadConfig.pcAndConsole,
    'combine-detail-kpitends': downloadConfig.pcAndConsoleDetailKpiTrends,
    'combine-detail-kpitends-hourly': downloadConfig.pcAndConsoleDetailKpiTrendsHourly,
    'combine-stats-countries': downloadConfig.pcAndConsoleDetailKpiTrends,
    'combine-stats-countries-pcu': downloadConfig.pcAndConsoleDetailKpiTrends,
    'combine-stats-countries-japan-sales': downloadConfig.pcAndConsoleDetailKpiTrends,
    'pc-console-detail-store-rank': downloadConfig.pcAndConsoleDetailStoreRank,
    'companies-revenue': downloadConfig.companies,
    'companies-downloads': downloadConfig.companies,
    'companies-mentions': downloadConfig.companies,
    'companies-detail': downloadConfig.companiesDetail,
    'mobile-activeuser': downloadConfig.mobileUniversal,
    'mobile-activeuser-studio': downloadConfig.mobileUniversal,
    'mobile-revenue': downloadConfig.mobileUniversal,
    'mobile-downloads': downloadConfig.mobileUniversal,
    'mobile-rpd': downloadConfig.mobileUniversal,
    'mobile-retention': downloadConfig.mobileUniversal,
    'mobile-databrain': downloadConfig.mobileUniversal,
    'mobile-newRelease': downloadConfig.mobileNewGame,
    'mobile-upcoming': downloadConfig.mobileNewGame,
    'mobile-demographics': downloadConfig.mobileDemographics,
    'pc-DigitalRevenue': downloadConfig.pcDigitalRevenue,
    'pc-PhysicalRevenue': downloadConfig.pcUniversal,
    'pc-MAU': downloadConfig.pcUniversal,
    'pc-SteamSales': downloadConfig.pcUniversal,
    'pc-LifeTimePlayers': downloadConfig.pcOthers,
    'pc-MetricsTable': downloadConfig.pcMetricsTable,
    'pc-PCU': downloadConfig.pcUniversal,
    'pc-newRelease': downloadConfig.pcOthers,
    'pc-upcoming': downloadConfig.pcOthers,
    'mobile-store-rank-more': downloadConfig.storeRankMobile,
    'pc-store-rank-more': downloadConfig.storeRankPC,
    'console-store-rank-more': downloadConfig.storeRankConsole,
    'marketProfile-stats': downloadConfig.marketProfileStats,
    'genre-profile': downloadConfig.genreProfile,
    'genre-profile-detail': downloadConfig.genreProfileStats,
    'mobile-benchmark-kpi_data': downloadConfig.mobileBenchmark,
    'mobile-benchmark-player_activity': downloadConfig.mobileBenchmark,
    'mobile-benchmark-social_channels': downloadConfig.mobileBenchmark,
    'mobile-benchmark-basic_info': downloadConfig.mobileBenchmark,
    'overlap-mobile-games': downloadConfig.mobileOverlap,
    'streaming-anchors-more': downloadConfig.streamingInfluencer,
    'streaming-games-top': downloadConfig.streamingInfluencer,
    'following-currentgame-mobile': downloadConfig.followingCurrentMobile,
    'following-currentgame': downloadConfig.followingCurrentPC,
    'following-newcompany': downloadConfig.followingCurrentPC,
    'compare-mobile-store-rank': downloadConfig.compareMobileStoreRank,
    'compare-mobile': downloadConfig.compareMobile,
    'combine-stats-countries-compare': downloadConfig.compareMutilDimension,
    'combine-detail-steamoverlap': downloadConfig.pcAndConsoleDetailSteamOverlap,
    'combine-detail-othervendor-overlap': downloadConfig.pcAndConsoleDetailOtherVendorOverlap,
    mobileMarketProfileTopCharts: downloadConfig.mobileMarketProfileTopCharts,
  } as any;
  return DOWNLOAD_SET_MAP[businessType] || [];
};
